import React, { useState, useEffect, useCallback } from 'react';
import { colors, authHeaders, isAdmin } from '../utils/theme';
import { abrirVerComo } from '../utils/verComo';
import { Users, Plus, Edit2, Trash2, Search, X, Eye, EyeOff, Shield, UserCheck, UserX, Download, CheckSquare, Square, AlertCircle, Info } from 'lucide-react';

const PERFIS_DISPONIVEIS = [
  'ADMIN',
  'Gerente Financeiro',
  'Operador Financeiro',
  'Gestor de Acessos',
  'Operador de Acessos',
  'Requisitante',
  'Fidelizado Uso Pessoal',
  'Operador Externo',
  'Com Lista de Cartoes',
  'Titular',
];

const PERFIL_DESCRICAO = {
  'ADMIN': 'Acesso total ao sistema',
  'Gerente Financeiro': 'Dashboard, cadastros, lancamentos, conciliacao, crise, painel investidor',
  'Operador Financeiro': 'Dashboard, cartoes, lancamentos, conciliacao, modulos de crise',
  'Gestor de Acessos': 'Dashboard, gestao de usuarios',
  'Operador de Acessos': 'Dashboard, visualizar usuarios',
  'Requisitante': 'Dashboard, consultar cartoes',
  'Fidelizado Uso Pessoal': 'Dashboard, consultar cartoes',
  'Operador Externo': 'Dashboard, cartoes, lancamentos',
  'Com Lista de Cartoes': 'Dashboard, consultar cartoes',
  'Titular': 'Dashboard, cartoes, lancamentos, crise — filtrado somente para o titular vinculado',
};

const API = window.location.origin + '/api';

const emptyForm = { login: '', senha: '', nome: '', sobrenome: '', telefone: '', email: '', perfis: [], ativo: true, titular_id: '', manager_login: '', acesso_crise: false, acesso_cofre: false, acesso_apuracao: false };

export default function UsuariosPage() {
  const [usuarios, setUsuarios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ ...emptyForm });
  const [editId, setEditId] = useState(null);
  const [showSenha, setShowSenha] = useState(false);
  // "Ver como" (03/11/2026): ADMIN abre o sistema como o usuário, em outra aba.
  const admin = isAdmin();
  const meuId = (() => { try { return JSON.parse(localStorage.getItem('xm_usuario') || '{}').id; } catch { return null; } })();
  const verComo = async (u) => {
    try {
      await abrirVerComo(async () => {
        const r = await fetch(`${API}/auth/ver-como`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ usuario_id: u.id }) });
        const d = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(d.error || `HTTP ${r.status}`);
        return d;
      });
    } catch (e) { alert('Não deu pra abrir o ver como: ' + e.message); }
  };
  const [search, setSearch] = useState('');
  const [erro, setErro] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('todos'); // todos, ativos, inativos, sem_perfil
  const [showImportModal, setShowImportModal] = useState(false);
  const [managerUsers, setManagerUsers] = useState([]);
  const [selectedImport, setSelectedImport] = useState(new Set());
  const [importLoading, setImportLoading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showPerfilInfo, setShowPerfilInfo] = useState(false);
  const [titulares, setTitulares] = useState([]);
  const [managerLoginOpcoes, setManagerLoginOpcoes] = useState([]); // lista de logins do Manager para autocomplete
  const [managerLoginLoading, setManagerLoginLoading] = useState(false);
  const [showManagerLoginDropdown, setShowManagerLoginDropdown] = useState(false);

  const fetchUsuarios = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/usuarios`, { headers: authHeaders() });
      if (res.ok) setUsuarios(await res.json());
    } catch (e) { console.error('Erro:', e); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchUsuarios(); }, [fetchUsuarios]);

  // Carregar titulares para o select
  useEffect(() => {
    fetch(`${API}/titulares`, { headers: authHeaders() })
      .then(r => r.ok ? r.json() : []).then(setTitulares).catch(() => {});
  }, []);

  const openNew = () => { setForm({ ...emptyForm }); setEditId(null); setShowSenha(false); setErro(''); setShowModal(true); setShowManagerLoginDropdown(false); };

  const openEdit = (u) => {
    setForm({
      login: u.login, senha: '', nome: u.nome, sobrenome: u.sobrenome || '',
      telefone: u.telefone || '', email: u.email || '',
      perfis: u.perfis || [], ativo: u.ativo === 1 || u.ativo === true,
      titular_id: u.titular_id || '', manager_login: u.manager_login || '',
      acesso_crise: !!u.acesso_crise,
      acesso_cofre: !!u.acesso_cofre,
      acesso_apuracao: !!u.acesso_apuracao,
    });
    setEditId(u.id); setShowSenha(false); setErro(''); setShowModal(true);
    setShowManagerLoginDropdown(false);
  };

  const togglePerfil = (perfil) => {
    setForm(prev => ({
      ...prev,
      perfis: prev.perfis.includes(perfil) ? prev.perfis.filter(p => p !== perfil) : [...prev.perfis, perfil]
    }));
  };

  // Shared: busca usuários do Manager (verifica autenticação primeiro)
  const fetchManagerUsers = async () => {
    const statusRes = await fetch(`${API}/xmiles/status`, { headers: authHeaders() });
    const statusData = await statusRes.json();
    if (!statusData.authenticated) throw new Error('Manager nao autenticado. Faca login em Importacao/Sync.');
    const res = await fetch(`${API}/xmiles/usuarios-manager`, { headers: authHeaders() });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error || 'Endpoint nao disponivel no Manager.');
    }
    return res.json();
  };

  const buscarManagerLogins = async () => {
    setManagerLoginLoading(true);
    setShowManagerLoginDropdown(true);
    try {
      const users = await fetchManagerUsers();
      setManagerLoginOpcoes(users);
    } catch (e) {
      setManagerLoginOpcoes([{ login: '__erro__', nome: e.message }]);
    } finally {
      setManagerLoginLoading(false);
    }
  };

  const salvar = async () => {
    if (!form.login || !form.nome) { setErro('Login e Nome sao obrigatorios'); return; }
    if (!editId && !form.senha) { setErro('Senha e obrigatoria para novo usuario'); return; }
    setErro('');
    try {
      const body = { ...form };
      if (editId && !body.senha) delete body.senha;
      const url = editId ? `${API}/usuarios/${editId}` : `${API}/usuarios`;
      const res = await fetch(url, { method: editId ? 'PUT' : 'POST', headers: authHeaders(), body: JSON.stringify(body) });
      const data = await res.json();
      if (!res.ok) { setErro(data.error || 'Erro ao salvar'); return; }
      setShowModal(false);
      fetchUsuarios();
    } catch (e) { setErro('Erro de conexao'); }
  };

  const desativar = async (id) => {
    if (!window.confirm('Deseja desativar este usuario?')) return;
    try { await fetch(`${API}/usuarios/${id}`, { method: 'DELETE', headers: authHeaders() }); fetchUsuarios(); } catch (e) {}
  };

  // === IMPORTACAO SELETIVA ===
  const abrirImportacao = async () => {
    setImportLoading(true);
    setImportResult(null);
    setSelectedImport(new Set());
    setManagerUsers([]);
    setShowImportModal(true);

    try {
      const users = await fetchManagerUsers();
      const loginsLocais = new Set(usuarios.map(u => u.login));
      setManagerUsers(users.map(u => ({ ...u, jaExiste: loginsLocais.has(u.login) })));
    } catch (e) {
      setImportResult({ error: e.message });
    } finally {
      setImportLoading(false);
    }
  };

  const toggleSelectImport = (login) => {
    setSelectedImport(prev => {
      const next = new Set(prev);
      next.has(login) ? next.delete(login) : next.add(login);
      return next;
    });
  };

  const selecionarTodosNovos = () => {
    const novos = managerUsers.filter(u => !u.jaExiste).map(u => u.login);
    setSelectedImport(new Set(novos));
  };

  const importarSelecionados = async () => {
    if (selectedImport.size === 0) return;
    setImportLoading(true);
    const selecionados = managerUsers.filter(u => selectedImport.has(u.login));
    try {
      const res = await fetch(`${API}/usuarios/importar-manager`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({ usuarios: selecionados }),
      });
      const data = await res.json();
      setImportResult(data);
      fetchUsuarios();
    } catch (e) {
      setImportResult({ error: 'Erro: ' + e.message });
    }
    setImportLoading(false);
  };

  // === FILTROS ===
  const filtrados = usuarios.filter(u => {
    if (filtroStatus === 'ativos' && !(u.ativo === 1 || u.ativo === true)) return false;
    if (filtroStatus === 'inativos' && (u.ativo === 1 || u.ativo === true)) return false;
    if (filtroStatus === 'sem_perfil' && (u.perfis || []).length > 0) return false;
    if (!search) return true;
    const s = search.toLowerCase();
    return (u.nome || '').toLowerCase().includes(s) || (u.login || '').toLowerCase().includes(s) || (u.email || '').toLowerCase().includes(s);
  });

  const totalAtivos = usuarios.filter(u => u.ativo === 1 || u.ativo === true).length;
  const totalSemPerfil = usuarios.filter(u => (u.perfis || []).length === 0).length;

  const inputStyle = {
    width: '100%', padding: '10px 12px', borderRadius: 8,
    border: `1px solid ${colors.border}`, background: colors.bg,
    color: colors.text, fontSize: 13, outline: 'none', boxSizing: 'border-box',
  };
  const labelStyle = { display: 'block', fontSize: 11, fontWeight: 600, color: colors.textMuted, marginBottom: 4 };

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: colors.text, display: 'flex', alignItems: 'center', gap: 10 }}>
            <Users size={24} /> Gestao de Usuarios
          </h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: colors.textDim }}>
            {usuarios.length} usuarios | {totalAtivos} ativos | {totalSemPerfil > 0 && <span style={{ color: colors.warning }}>{totalSemPerfil} sem perfil</span>}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setShowPerfilInfo(true)} style={{
            padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.card, color: colors.info, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}><Info size={14} /> Politica de Acesso</button>
          <button onClick={abrirImportacao} style={{
            padding: '10px 14px', borderRadius: 8, border: `1px solid ${colors.border}`,
            background: colors.card, color: colors.textMuted, fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}><Download size={14} /> Importar do Manager</button>
          <button onClick={openNew} style={{
            padding: '10px 20px', borderRadius: 8, border: 'none',
            background: colors.primary, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6,
          }}><Plus size={16} /> Novo Usuario</button>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 350 }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: colors.textDim }} />
          <input placeholder="Buscar por nome, login ou email..." value={search} onChange={e => setSearch(e.target.value)}
            style={{ ...inputStyle, paddingLeft: 36 }} />
        </div>
        {['todos', 'ativos', 'inativos', 'sem_perfil'].map(f => (
          <button key={f} onClick={() => setFiltroStatus(f)} style={{
            padding: '8px 14px', borderRadius: 8, fontSize: 12, fontWeight: 600, cursor: 'pointer',
            border: filtroStatus === f ? `1px solid ${colors.primary}` : `1px solid ${colors.border}`,
            background: filtroStatus === f ? `${colors.primary}20` : 'transparent',
            color: filtroStatus === f ? colors.primaryLight : colors.textMuted,
          }}>
            {f === 'todos' ? 'Todos' : f === 'ativos' ? 'Ativos' : f === 'inativos' ? 'Inativos' : 'Sem Perfil'}
          </button>
        ))}
      </div>

      {/* Alerta sem perfil */}
      {totalSemPerfil > 0 && (
        <div style={{
          padding: '10px 16px', marginBottom: 16, borderRadius: 8,
          background: `${colors.warning}10`, border: `1px solid ${colors.warning}30`,
          color: colors.warning, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertCircle size={16} />
          {totalSemPerfil} usuario(s) sem perfil de acesso — nao conseguem fazer login ate receber um perfil.
        </div>
      )}

      {/* Table */}
      <div style={{ background: colors.card, borderRadius: 12, border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: `${colors.primary}10` }}>
              {['Login', 'Manager', 'Nome', 'Email', 'Perfis', 'Titular', 'Status', 'Ultimo Acesso', 'Acoes'].map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: colors.textMuted, fontSize: 11, textTransform: 'uppercase', borderBottom: `1px solid ${colors.border}` }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: colors.textDim }}>Carregando...</td></tr>
            ) : filtrados.length === 0 ? (
              <tr><td colSpan={9} style={{ padding: 40, textAlign: 'center', color: colors.textDim }}>Nenhum usuario encontrado</td></tr>
            ) : filtrados.map(u => (
              <tr key={u.id} style={{ borderBottom: `1px solid ${colors.border}22` }}
                onMouseEnter={e => e.currentTarget.style.background = `${colors.primary}08`}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <td style={{ padding: '10px 14px', fontWeight: 600, color: colors.text }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%', flexShrink: 0,
                      background: `linear-gradient(135deg, ${colors.primary}, ${colors.info})`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 12, fontWeight: 700,
                    }}>{(u.nome || 'U')[0].toUpperCase()}</div>
                    {u.login}
                  </div>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  {u.manager_login ? (
                    <span title={`Login no Manager: ${u.manager_login}`} style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 4,
                      background: `${colors.success}15`, color: colors.success, fontWeight: 600, fontFamily: 'monospace',
                    }}>{u.manager_login}</span>
                  ) : (
                    <span style={{ fontSize: 11, color: colors.textDim }}>—</span>
                  )}
                </td>
                <td style={{ padding: '10px 14px', color: colors.text }}>{u.nome} {u.sobrenome || ''}</td>
                <td style={{ padding: '10px 14px', color: colors.textMuted }}>{u.email || '-'}</td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(u.perfis || []).length === 0 ? (
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: `${colors.warning}20`, color: colors.warning, fontWeight: 600 }}>SEM PERFIL</span>
                    ) : (u.perfis || []).map(p => (
                      <span key={p} style={{
                        fontSize: 10, padding: '2px 6px', borderRadius: 4,
                        background: p === 'ADMIN' ? `${colors.danger}20` : `${colors.primary}20`,
                        color: p === 'ADMIN' ? colors.danger : colors.primaryLight, fontWeight: 600,
                      }}>{p}</span>
                    ))}
                    {/* Cofre e Crise não são perfis, são flags por usuário — e
                        viviam escondidas dentro do modal de edição. Sem isso na
                        lista, saber quem tem acesso ao Cofre exigia abrir um a
                        um, e era impossível conferir se a liberação pegou. */}
                    {u.acesso_cofre && (
                      <span title="Vê número, validade e CVV dos cartões do escopo dele"
                        style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: `${colors.warning}20`, color: colors.warning, fontWeight: 600 }}>COFRE</span>
                    )}
                    {u.acesso_crise && (
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: `${colors.danger}15`, color: colors.danger, fontWeight: 600 }}>CRISE</span>
                    )}
                    {u.acesso_apuracao && (
                      <span title="Vê a apuração de todos os investidores (Remuneração)"
                        style={{ fontSize: 10, padding: '2px 6px', borderRadius: 4, background: `${colors.success}18`, color: colors.success, fontWeight: 600 }}>APURAÇÃO</span>
                    )}
                  </div>
                </td>
                <td style={{ padding: '10px 14px', color: colors.textMuted, fontSize: 12 }}>
                  {u.titular_nome ? (
                    <span style={{ fontSize: 11, padding: '2px 6px', borderRadius: 4, background: `${colors.info}15`, color: colors.info, fontWeight: 600 }}>{u.titular_nome}</span>
                  ) : '-'}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    display: 'inline-flex', alignItems: 'center', gap: 4,
                    fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 6,
                    background: (u.ativo === 1 || u.ativo === true) ? `${colors.success}15` : `${colors.danger}15`,
                    color: (u.ativo === 1 || u.ativo === true) ? colors.success : colors.danger,
                  }}>
                    {(u.ativo === 1 || u.ativo === true) ? <><UserCheck size={12} /> Ativo</> : <><UserX size={12} /> Inativo</>}
                  </span>
                </td>
                <td style={{ padding: '10px 14px', color: colors.textDim, fontSize: 12 }}>
                  {u.ultimo_acesso ? new Date(u.ultimo_acesso).toLocaleString('pt-BR') : 'Nunca'}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div style={{ display: 'flex', gap: 6 }}>
                    {admin && u.id !== meuId && (u.perfis || []).length > 0 && (
                      <button onClick={() => verComo(u)} title={`Abrir o sistema como ${u.login}, em outra aba`} style={{
                        padding: '6px 8px', borderRadius: 6, border: `1px solid ${colors.warning}55`,
                        background: `${colors.warning}15`, color: colors.warning, cursor: 'pointer',
                        display: 'flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                      }}><Eye size={13} /> Ver como</button>
                    )}
                    <button onClick={() => openEdit(u)} title="Editar" style={{
                      padding: '6px 8px', borderRadius: 6, border: `1px solid ${colors.border}`,
                      background: 'transparent', color: colors.primary, cursor: 'pointer',
                    }}><Edit2 size={14} /></button>
                    {u.id !== 1 && (
                      <button onClick={() => desativar(u.id)} title="Desativar" style={{
                        padding: '6px 8px', borderRadius: 6, border: `1px solid ${colors.border}`,
                        background: 'transparent', color: colors.danger, cursor: 'pointer',
                      }}><Trash2 size={14} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ============ MODAL CRIAR/EDITAR ============ */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowModal(false)}>
          <div style={{
            width: 700, maxHeight: '90vh', overflowY: 'auto',
            background: colors.card, borderRadius: 16, border: `1px solid ${colors.border}`,
            padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: colors.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Shield size={20} color={colors.primary} /> {editId ? 'Editar Usuario' : 'Novo Usuario'}
              </h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'transparent', border: 'none', color: colors.textDim, cursor: 'pointer' }}><X size={20} /></button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
              <div>
                <label style={labelStyle}>Login *</label>
                <input value={form.login} onChange={e => setForm({ ...form, login: e.target.value })} style={inputStyle} placeholder="login.usuario" />
              </div>
              <div>
                <label style={labelStyle}>{editId ? 'Nova Senha (vazio = manter)' : 'Senha *'}</label>
                <div style={{ position: 'relative' }}>
                  <input type={showSenha ? 'text' : 'password'} value={form.senha}
                    onChange={e => setForm({ ...form, senha: e.target.value })}
                    style={{ ...inputStyle, paddingRight: 36 }} placeholder={editId ? '(manter atual)' : 'Senha'} />
                  <button type="button" onClick={() => setShowSenha(!showSenha)}
                    style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: colors.textDim, padding: 4 }}>
                    {showSenha ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div>
                <label style={labelStyle}>Nome *</label>
                <input value={form.nome} onChange={e => setForm({ ...form, nome: e.target.value })} style={inputStyle} placeholder="Nome" />
              </div>
              <div>
                <label style={labelStyle}>Sobrenome</label>
                <input value={form.sobrenome} onChange={e => setForm({ ...form, sobrenome: e.target.value })} style={inputStyle} placeholder="Sobrenome" />
              </div>
              <div>
                <label style={labelStyle}>Telefone</label>
                <input value={form.telefone} onChange={e => setForm({ ...form, telefone: e.target.value })} style={inputStyle} placeholder="(XX) XXXXX-XXXX" />
              </div>
              <div>
                <label style={labelStyle}>E-mail</label>
                <input value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} style={inputStyle} placeholder="email@exemplo.com" type="email" />
              </div>
              <div style={{ gridColumn: 'span 2' }}>
                <label style={labelStyle}>
                  Login no XMiles Manager
                  <span style={{ color: colors.info, fontWeight: 400, marginLeft: 6 }}>— usado como responsavel nos lancamentos criados pelo sistema</span>
                </label>
                <div style={{ display: 'flex', gap: 8, position: 'relative' }}>
                  <input
                    value={form.manager_login}
                    onChange={e => { setForm({ ...form, manager_login: e.target.value }); setShowManagerLoginDropdown(false); }}
                    style={{ ...inputStyle, flex: 1 }}
                    placeholder="ex: joao.silva (login exato no Manager)"
                  />
                  <button type="button" onClick={buscarManagerLogins} disabled={managerLoginLoading} style={{
                    padding: '8px 14px', borderRadius: 8, border: `1px solid ${colors.info}50`,
                    background: `${colors.info}15`, color: colors.info, fontSize: 12, fontWeight: 600,
                    cursor: managerLoginLoading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', flexShrink: 0,
                  }}>
                    {managerLoginLoading ? 'Buscando...' : '🔍 Buscar no Manager'}
                  </button>
                  {showManagerLoginDropdown && managerLoginOpcoes.length > 0 && (
                    <div style={{
                      position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, marginTop: 4,
                      background: colors.card, border: `1px solid ${colors.border}`, borderRadius: 10,
                      boxShadow: '0 8px 24px rgba(0,0,0,0.4)', maxHeight: 220, overflowY: 'auto',
                    }}>
                      <div style={{ padding: '8px 12px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 11, color: colors.textDim, fontWeight: 600 }}>SELECIONE O LOGIN DO MANAGER</span>
                        <button onClick={() => setShowManagerLoginDropdown(false)} style={{ background: 'none', border: 'none', color: colors.textDim, cursor: 'pointer', padding: 2 }}><X size={14} /></button>
                      </div>
                      {managerLoginOpcoes.map(u => (
                        u.login === '__erro__' ? (
                          <div key="erro" style={{ padding: '10px 14px', color: colors.danger, fontSize: 12 }}>{u.nome}</div>
                        ) : (
                          <div key={u.login} onClick={() => { setForm(prev => ({ ...prev, manager_login: u.login })); setShowManagerLoginDropdown(false); }}
                            style={{ padding: '10px 14px', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${colors.border}15` }}
                            onMouseEnter={e => e.currentTarget.style.background = `${colors.primary}15`}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <div>
                              <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>{u.login}</div>
                              <div style={{ fontSize: 11, color: colors.textMuted }}>{u.nome} {u.sobrenome || ''}</div>
                            </div>
                            {u.email && <div style={{ fontSize: 11, color: colors.textDim }}>{u.email}</div>}
                          </div>
                        )
                      ))}
                    </div>
                  )}
                </div>
                <div style={{ marginTop: 4, fontSize: 11, color: form.manager_login ? colors.success : colors.textDim }}>
                  {form.manager_login
                    ? <>✓ Login Manager configurado: <b>{form.manager_login}</b></>
                    : 'Se nao configurado, lancamentos usarao o login padrao do sistema.'}
                </div>
              </div>
            </div>

            {/* Ativo toggle */}
            <div style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ ...labelStyle, margin: 0 }}>Status:</label>
              <button onClick={() => setForm({ ...form, ativo: !form.ativo })} style={{
                padding: '6px 14px', borderRadius: 6, border: 'none', cursor: 'pointer',
                background: form.ativo ? `${colors.success}20` : `${colors.danger}20`,
                color: form.ativo ? colors.success : colors.danger, fontSize: 12, fontWeight: 600,
              }}>{form.ativo ? 'Ativo' : 'Inativo'}</button>
              {!form.ativo && <span style={{ fontSize: 11, color: colors.textDim }}>Usuario inativo nao consegue fazer login</span>}
            </div>

            {/* Perfis */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ ...labelStyle, marginBottom: 10 }}>
                Perfis de Acesso
                {form.perfis.length === 0 && <span style={{ color: colors.warning, marginLeft: 8 }}>* Sem perfil = sem acesso ao sistema</span>}
              </label>
              <div style={{
                display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8,
                padding: 16, borderRadius: 10, background: colors.bg, border: `1px solid ${colors.border}`,
              }}>
                {PERFIS_DISPONIVEIS.map(perfil => {
                  const checked = form.perfis.includes(perfil);
                  return (
                    <label key={perfil} style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer',
                      padding: '8px 10px', borderRadius: 8,
                      background: checked ? `${colors.primary}15` : 'transparent',
                      border: `1px solid ${checked ? colors.primary + '50' : colors.border}`,
                    }}>
                      <input type="checkbox" checked={checked} onChange={() => togglePerfil(perfil)} style={{ accentColor: colors.primary, marginTop: 2 }} />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: checked ? 600 : 400, color: checked ? colors.text : colors.textMuted }}>{perfil}</div>
                        <div style={{ fontSize: 10, color: colors.textDim, marginTop: 2 }}>{PERFIL_DESCRICAO[perfil]}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>

            {/* Vinculo com Titular (se perfil Titular selecionado) */}
            {form.perfis.includes('Titular') && (
              <div style={{ marginBottom: 16 }}>
                <label style={{ ...labelStyle, marginBottom: 6 }}>
                  Titular Vinculado * <span style={{ color: colors.info, fontWeight: 400 }}>— este usuario so vera os dados deste titular</span>
                </label>
                <select
                  value={form.titular_id}
                  onChange={e => setForm({ ...form, titular_id: e.target.value ? parseInt(e.target.value) : '' })}
                  style={{ ...inputStyle, cursor: 'pointer' }}
                >
                  <option value="">Selecione o titular...</option>
                  {titulares.map(t => (
                    <option key={t.id} value={t.id}>{t.nome}</option>
                  ))}
                </select>
                {!form.titular_id && (
                  <div style={{ marginTop: 6, fontSize: 11, color: colors.warning }}>Obrigatorio vincular a um titular para o perfil Titular funcionar</div>
                )}
              </div>
            )}

            {/* Cofre de cartões (14/09/2026): número, validade e CVV. Só quem tem este
                flag (ou é o dono do cartão) enxerga; toda consulta é auditada. */}
            {/* As cores tinham se perdido numa edição anterior (sobrou
                background:"10" e border:"1px solid 40", que o navegador
                descarta) — o bloco ficava sem destaque e passava batido. */}
            <div style={{ marginBottom: 14, padding: '12px 14px', background: `${colors.warning}10`,
              border: `1px solid ${colors.warning}40`, borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!form.acesso_cofre}
                  onChange={e => setForm({ ...form, acesso_cofre: e.target.checked })}
                  style={{ marginTop: 3, cursor: 'pointer' }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>Habilitar acesso ao Cofre de cartões</div>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 3 }}>
                    Vê número, validade e CVV dos cartões do escopo dele (revelar é registrado com login, IP e hora).
                    O dono do cartão já vê os próprios sem esta opção.
                  </div>
                </div>
              </label>
            </div>
            {/* Acesso aos painéis de crise — disponível pra QUALQUER perfil (admin sempre tem) */}
            <div style={{ marginBottom: 20, padding: '12px 14px', background: `${colors.warning}10`,
              border: `1px solid ${colors.warning}40`, borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!form.acesso_crise}
                  onChange={e => setForm({ ...form, acesso_crise: e.target.checked })}
                  style={{ marginTop: 3, cursor: 'pointer' }}
                />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>
                    Habilitar painéis de Gestão de Crise
                  </div>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 3 }}>
                    Quando marcado, este usuário vê os módulos de crise (Pagamentos, Encargos, Parcelamentos, Anuidades, Negociações),
                    independente do perfil. Sem esta opção, não vê nenhum módulo de crise (vale até para Gerente Financeiro).
                  </div>
                </div>
              </label>
            </div>
            {/* Apuração de todos os investidores (03/11/2026) */}
            <div style={{ marginBottom: 20, padding: '12px 14px', background: `${colors.success}10`,
              border: `1px solid ${colors.success}40`, borderRadius: 8 }}>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={!!form.acesso_apuracao}
                  onChange={e => setForm({ ...form, acesso_apuracao: e.target.checked })}
                  style={{ marginTop: 3, cursor: 'pointer' }} />
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: colors.text }}>Ver a apuração de todos os investidores</div>
                  <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 3 }}>
                    Abre a Remuneração de Investidor com todos os investidores, mesmo os de fora da operação dele.
                    Só leitura: fechar, pagar e mudar taxa seguem pelo perfil e pela operação de cada um.
                  </div>
                </div>
              </label>
            </div>

            {erro && (
              <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 8, background: `${colors.danger}15`, border: `1px solid ${colors.danger}30`, color: colors.danger, fontSize: 13 }}>{erro}</div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button onClick={() => setShowModal(false)} style={{
                padding: '10px 20px', borderRadius: 8, border: `1px solid ${colors.border}`,
                background: 'transparent', color: colors.textMuted, fontSize: 13, cursor: 'pointer',
              }}>Cancelar</button>
              <button onClick={salvar} style={{
                padding: '10px 24px', borderRadius: 8, border: 'none',
                background: colors.primary, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>{editId ? 'Salvar' : 'Criar Usuario'}</button>
            </div>
          </div>
        </div>
      )}

      {/* ============ MODAL IMPORTACAO SELETIVA ============ */}
      {showImportModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowImportModal(false)}>
          <div style={{
            width: 800, maxHeight: '85vh', display: 'flex', flexDirection: 'column',
            background: colors.card, borderRadius: 16, border: `1px solid ${colors.border}`,
            boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }} onClick={e => e.stopPropagation()}>
            {/* Header */}
            <div style={{ padding: '20px 24px', borderBottom: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: colors.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Download size={20} color={colors.primary} /> Importar do Manager
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: 12, color: colors.textDim }}>
                  Selecione quais usuarios deseja importar. Importados entram <b style={{ color: colors.warning }}>inativos e sem perfil</b>.
                </p>
              </div>
              <button onClick={() => setShowImportModal(false)} style={{ background: 'transparent', border: 'none', color: colors.textDim, cursor: 'pointer' }}><X size={20} /></button>
            </div>

            {/* Content */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
              {importLoading && !managerUsers.length ? (
                <div style={{ padding: 40, textAlign: 'center', color: colors.textDim }}>Buscando usuarios do Manager...</div>
              ) : importResult?.error ? (
                <div style={{ padding: '16px', borderRadius: 8, background: `${colors.danger}15`, border: `1px solid ${colors.danger}30`, color: colors.danger, fontSize: 13 }}>
                  {importResult.error}
                </div>
              ) : importResult?.importados !== undefined ? (
                <div style={{ padding: '16px', borderRadius: 8, background: `${colors.success}15`, border: `1px solid ${colors.success}30`, color: colors.success, fontSize: 13 }}>
                  <b>{importResult.importados}</b> usuarios importados | <b>{importResult.existentes}</b> ja existiam
                  <br /><span style={{ fontSize: 11, color: colors.textDim }}>Senha padrao: <b>trocar123</b> — Usuarios importados estao INATIVOS. Configure perfil e ative cada um.</span>
                </div>
              ) : managerUsers.length > 0 ? (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontSize: 13, color: colors.textMuted }}>
                      {managerUsers.length} usuarios no Manager | {managerUsers.filter(u => !u.jaExiste).length} novos | {selectedImport.size} selecionados
                    </span>
                    <button onClick={selecionarTodosNovos} style={{
                      padding: '6px 12px', borderRadius: 6, border: `1px solid ${colors.border}`,
                      background: 'transparent', color: colors.primary, fontSize: 12, cursor: 'pointer', fontWeight: 600,
                    }}>Selecionar Todos Novos</button>
                  </div>
                  <div style={{ borderRadius: 10, border: `1px solid ${colors.border}`, overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                      <thead>
                        <tr style={{ background: `${colors.primary}10` }}>
                          <th style={{ padding: '10px 12px', width: 40, textAlign: 'center', borderBottom: `1px solid ${colors.border}` }}></th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: colors.textMuted, fontSize: 11, borderBottom: `1px solid ${colors.border}` }}>LOGIN</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: colors.textMuted, fontSize: 11, borderBottom: `1px solid ${colors.border}` }}>NOME</th>
                          <th style={{ padding: '10px 12px', textAlign: 'left', fontWeight: 600, color: colors.textMuted, fontSize: 11, borderBottom: `1px solid ${colors.border}` }}>EMAIL</th>
                          <th style={{ padding: '10px 12px', textAlign: 'center', fontWeight: 600, color: colors.textMuted, fontSize: 11, borderBottom: `1px solid ${colors.border}` }}>STATUS</th>
                        </tr>
                      </thead>
                      <tbody>
                        {managerUsers.map(u => (
                          <tr key={u.login} style={{
                            borderBottom: `1px solid ${colors.border}15`,
                            opacity: u.jaExiste ? 0.5 : 1,
                            background: selectedImport.has(u.login) ? `${colors.primary}10` : 'transparent',
                          }}>
                            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                              {u.jaExiste ? (
                                <span style={{ color: colors.textDim }}>-</span>
                              ) : (
                                <button onClick={() => toggleSelectImport(u.login)} style={{
                                  background: 'none', border: 'none', cursor: 'pointer',
                                  color: selectedImport.has(u.login) ? colors.primary : colors.textDim,
                                }}>
                                  {selectedImport.has(u.login) ? <CheckSquare size={16} /> : <Square size={16} />}
                                </button>
                              )}
                            </td>
                            <td style={{ padding: '8px 12px', fontWeight: 600, color: colors.text }}>{u.login}</td>
                            <td style={{ padding: '8px 12px', color: colors.text }}>{u.nome} {u.sobrenome || ''}</td>
                            <td style={{ padding: '8px 12px', color: colors.textMuted }}>{u.email || '-'}</td>
                            <td style={{ padding: '8px 12px', textAlign: 'center' }}>
                              {u.jaExiste ? (
                                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: `${colors.success}15`, color: colors.success, fontWeight: 600 }}>Ja existe</span>
                              ) : (
                                <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 4, background: `${colors.info}15`, color: colors.info, fontWeight: 600 }}>Novo</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              ) : (
                <div style={{ padding: 40, textAlign: 'center', color: colors.textDim }}>Nenhum usuario encontrado no Manager</div>
              )}
            </div>

            {/* Footer */}
            {managerUsers.length > 0 && !importResult?.importados && (
              <div style={{ padding: '16px 24px', borderTop: `1px solid ${colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 12, color: colors.textDim }}>
                  Importados entram inativos e sem perfil ate voce configurar.
                </span>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => setShowImportModal(false)} style={{
                    padding: '10px 20px', borderRadius: 8, border: `1px solid ${colors.border}`,
                    background: 'transparent', color: colors.textMuted, fontSize: 13, cursor: 'pointer',
                  }}>Cancelar</button>
                  <button onClick={importarSelecionados} disabled={selectedImport.size === 0 || importLoading} style={{
                    padding: '10px 20px', borderRadius: 8, border: 'none',
                    background: selectedImport.size === 0 ? colors.textDim : colors.primary,
                    color: '#fff', fontSize: 13, fontWeight: 600, cursor: selectedImport.size === 0 ? 'not-allowed' : 'pointer',
                  }}>
                    {importLoading ? 'Importando...' : `Importar ${selectedImport.size} selecionado(s)`}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============ MODAL POLITICA DE ACESSO ============ */}
      {showPerfilInfo && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          onClick={() => setShowPerfilInfo(false)}>
          <div style={{
            width: 650, maxHeight: '80vh', overflowY: 'auto',
            background: colors.card, borderRadius: 16, border: `1px solid ${colors.border}`,
            padding: 28, boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
          }} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700, color: colors.text, display: 'flex', alignItems: 'center', gap: 8 }}>
                <Shield size={20} color={colors.info} /> Politica de Acesso por Perfil
              </h2>
              <button onClick={() => setShowPerfilInfo(false)} style={{ background: 'transparent', border: 'none', color: colors.textDim, cursor: 'pointer' }}><X size={20} /></button>
            </div>

            <div style={{ fontSize: 12, color: colors.textDim, marginBottom: 16 }}>
              Cada perfil define quais modulos do sistema o usuario pode acessar. Um usuario pode ter multiplos perfis — as permissoes sao somadas. <b style={{ color: colors.warning }}>Usuarios sem perfil nao conseguem fazer login.</b>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {PERFIS_DISPONIVEIS.map(perfil => (
                <div key={perfil} style={{
                  padding: '12px 16px', borderRadius: 10, background: colors.bg,
                  border: `1px solid ${colors.border}`,
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 4,
                      background: perfil === 'ADMIN' ? `${colors.danger}20` : `${colors.primary}20`,
                      color: perfil === 'ADMIN' ? colors.danger : colors.primaryLight, fontWeight: 700,
                    }}>{perfil}</span>
                  </div>
                  <div style={{ fontSize: 12, color: colors.textMuted }}>
                    {PERFIL_DESCRICAO[perfil]}
                  </div>
                </div>
              ))}
            </div>

            <div style={{ marginTop: 20, padding: '12px 16px', borderRadius: 8, background: `${colors.info}10`, border: `1px solid ${colors.info}30` }}>
              <div style={{ fontSize: 12, color: colors.info, fontWeight: 600, marginBottom: 4 }}>Regras importantes:</div>
              <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: colors.textMuted, lineHeight: 1.8 }}>
                <li><b>ADMIN</b> tem acesso total — bypassa todas as restricoes</li>
                <li>Usuario sem nenhum perfil <b>nao consegue fazer login</b></li>
                <li>Usuarios importados do Manager entram <b>inativos e sem perfil</b></li>
                <li>Multiplos perfis: as permissoes sao <b>somadas</b> (uniao)</li>
                <li>Desativar um usuario impede login imediatamente</li>
                <li>Perfil <b>Titular</b> requer vinculo com um titular — so vera dados do proprio titular em todas as paginas</li>
              </ul>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button onClick={() => setShowPerfilInfo(false)} style={{
                padding: '10px 20px', borderRadius: 8, border: 'none',
                background: colors.primary, color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
              }}>Entendi</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
